import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FilterType, UserData, UserCounts } from "../types/userTypes";
import type { UserDetails } from "@/api/users/types";

interface UserDataResponse {
  users: UserData[];
  total: number;
  userCounts: UserCounts;
}

export const useUserData = (currentPage: number, pageSize: number, searchTerm: string, activeFilter: FilterType, organizationId?: string) => {
  return useQuery({
    queryKey: ["users", currentPage, pageSize, searchTerm, activeFilter, organizationId],
    queryFn: async () => {
      try {
        console.log("Fetching users from Supabase");
        
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        console.log("Current user:", currentUser);
        
        // Fetch all users and rides to calculate distances
        let usersQuery = supabase.from('users').select('*, organization:organizations(name)').order('created_at', { ascending: false });
        
        // Apply organization filter if specified
        if (organizationId && organizationId !== 'all') {
          usersQuery = usersQuery.eq('organization_id', organizationId);
        }
        
        const [usersResponse, ridesResponse] = await Promise.all([
          usersQuery,
          supabase.from('rides').select('user_id, distance_traveled_km')
        ]);
        
        if (usersResponse.error) {
          console.error("Supabase query error:", usersResponse.error);
          toast.error("Error fetching users: " + usersResponse.error.message);
          throw usersResponse.error;
        }
        
        const users = usersResponse.data || [];
        const rides = ridesResponse.data || [];
        
        console.log("Raw users data with organization:", JSON.stringify(users, null, 2));
        console.log("Total users found in getUsers:", users.length);
        
        // Calculate total distance for each user
        const userDistances = new Map();
        const userRideCounts = new Map();
        
        rides.forEach(ride => {
          const userId = ride.user_id;
          const distance = ride.distance_traveled_km || 0;
          
          if (!userDistances.has(userId)) {
            userDistances.set(userId, 0);
            userRideCounts.set(userId, 0);
          }
          
          userDistances.set(userId, userDistances.get(userId) + distance);
          userRideCounts.set(userId, userRideCounts.get(userId) + 1);
        });
        
        // Map to UserDetails interface - using the correct role without mapping
        let transformedUsers: UserData[] = users.map(user => ({
          id: user.id,
          email: `${user.username.toLowerCase().replace(/\s+/g, "_")}@mail.com`,
          name: user.username,
          role: user.role,
          createdAt: new Date(user.created_at),
          totalRides: userRideCounts.get(user.id) || 0,
          totalDistance: userDistances.get(user.id) || 0,
          lastActive: new Date(),
          organizationName: user.organization?.name || null,
        }));
        
        if (activeFilter !== 'all') {
          transformedUsers = transformedUsers.filter(user => {
            switch (activeFilter) {
              case 'admin':
                return user.role === 'admin';
              case 'user':
                return user.role === 'user';
              default:
                return true;
            }
          });
        }
        
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          transformedUsers = transformedUsers.filter(user => 
            user.name.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower)
          );
        }
        
        const userCounts: UserCounts = {
          admin: transformedUsers.filter(user => user.role === 'admin').length,
          user: transformedUsers.filter(user => user.role === 'user').length
        };
        
        // Apply pagination
        const start = (currentPage - 1) * pageSize;
        const paginatedUsers = transformedUsers.slice(start, start + pageSize);
        
        return { 
          users: paginatedUsers, 
          total: transformedUsers.length,
          userCounts
        } as UserDataResponse;
      } catch (error) {
        console.error("Error fetching users from Supabase:", error);
        throw error;
      }
    },
  });
};
